import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";

import {ImmersionHeater} from "../../../interfaces/immersion_heater/ImmersionHeater";
import {LegionellosisProtection} from "../../../interfaces/immersion_heater/LegionellosisProtection";

import ToastService from "../../../service/ToastService";

import apiV2 from "../../../service/api/ApiV2";
import {EntityTypeEnum} from "../../../enums/EntityTypeEnum";
import {getErrorMessage} from "../../../service/AuthenticatedApi";
import i18next from "i18next";

interface ImmersionHeatersState {
  data: ImmersionHeater[];
  loading: boolean;
  error: string | null;
}

const initialState: ImmersionHeatersState = {
  data: [],
  loading: false,
  error: null,
};

export const updateLegionellosisProtection = createAsyncThunk<
  {legionellosisProtection: LegionellosisProtection},
  LegionellosisProtection,
  {rejectValue: string | null}
>("immersionHeater/updateLegionellosisProtection", async (legionellosisProtection, {rejectWithValue}) => {
  try {
    await apiV2.updateEntityProperty(EntityTypeEnum.IMMERSION_HEATER, legionellosisProtection.immersionHeaterId, [
      {name: "legionellosisProtection", value: legionellosisProtection.legionellosisProtection},
    ]);
    return {legionellosisProtection};
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, i18next.t("scada:plant.updates.legionellaError")));
  }
});

const getImmersionHeaterFromState = (state: ImmersionHeatersState, immersionHeaterId: string): ImmersionHeater => {
  const index = state.data.findIndex((x: ImmersionHeater) => x.id === immersionHeaterId);
  return state.data[index];
};

const immersionHeatersSlice = createSlice({
  name: "ImmersionHeaters",
  initialState,
  reducers: {
    setImmersionHeaters: (state, action) => {
      state.data = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateLegionellosisProtection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLegionellosisProtection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(updateLegionellosisProtection.fulfilled, (state, action) => {
        state.loading = false;
        const {legionellosisProtection} = action.payload;
        const immersionHeater = getImmersionHeaterFromState(state, legionellosisProtection.immersionHeaterId);
        immersionHeater.legionellosisProtection = legionellosisProtection.legionellosisProtection;
        ToastService.success(i18next.t("scada:plant.updates.legionella"));
      });
  },
});

export const {setImmersionHeaters} = immersionHeatersSlice.actions;

export default immersionHeatersSlice.reducer;
